import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { Bounty, User } from "@/lib/types";
import { Link } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, DollarSign, MessageSquare, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

function timeLeft(deadline: string): string {
	const diff = new Date(deadline).getTime() - Date.now();
	if (diff <= 0) return "Ended";
	const days = Math.floor(diff / (1000 * 60 * 60 * 24));
	const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
	if (days > 0) return `${days}d ${hours}h left`;
	return `${hours}h left`;
}

export function BountyList() {
	const [bounties, setBounties] = useState<Bounty[]>([]);
	const [users, setUsers] = useState<Map<string, User>>(new Map());
	const [filter, setFilter] = useState("all");
	const [search, setSearch] = useState("");

	useEffect(() => {
		api.getBounties().then(setBounties);
		api.getUsers().then((u) => {
			const map = new Map<string, User>();
			for (const user of u) map.set(user.id, user);
			setUsers(map);
		});
	}, []);

	const filtered = bounties.filter((b) => {
		if (filter !== "all" && b.status !== filter) return false;
		if (search && !b.title.toLowerCase().includes(search.toLowerCase()) && !b.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()))) return false;
		return true;
	});

	const stats = {
		total: bounties.length,
		open: bounties.filter((b) => b.status === "open").length,
		totalBounty: bounties.reduce((sum, b) => sum + b.bountyAmount, 0),
	};

	return (
		<div className="container mx-auto px-4 py-8 max-w-5xl">
			<div className="mb-8">
				<h1 className="text-3xl font-bold tracking-tight mb-2">Bounty Board</h1>
				<p className="text-muted-foreground">
					Post problems. Build solutions. Win bounties.
				</p>
			</div>

			<div className="grid grid-cols-3 gap-4 mb-8">
				<Card>
					<CardContent className="pt-4 pb-4 flex items-center gap-3">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10">
							<DollarSign className="h-5 w-5 text-emerald-600" />
						</div>
						<div>
							<p className="text-2xl font-bold">${stats.totalBounty.toLocaleString()}</p>
							<p className="text-xs text-muted-foreground">Total Bounties</p>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4 pb-4 flex items-center gap-3">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
							<MessageSquare className="h-5 w-5 text-blue-600" />
						</div>
						<div>
							<p className="text-2xl font-bold">{stats.total}</p>
							<p className="text-xs text-muted-foreground">Total Bounties</p>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-4 pb-4 flex items-center gap-3">
						<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10">
							<Clock className="h-5 w-5 text-amber-600" />
						</div>
						<div>
							<p className="text-2xl font-bold">{stats.open}</p>
							<p className="text-xs text-muted-foreground">Open Now</p>
						</div>
					</CardContent>
				</Card>
			</div>

			<div className="flex flex-col sm:flex-row gap-4 mb-6">
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input
						placeholder="Search bounties or tags..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="pl-9"
					/>
				</div>
				<Tabs value={filter} onValueChange={setFilter}>
					<TabsList>
						<TabsTrigger value="all">All</TabsTrigger>
						<TabsTrigger value="open">Open</TabsTrigger>
						<TabsTrigger value="in_review">In Review</TabsTrigger>
						<TabsTrigger value="awarded">Awarded</TabsTrigger>
					</TabsList>
				</Tabs>
			</div>

			<div className="space-y-3">
				{filtered.length === 0 && (
					<Card>
						<CardContent className="pt-6 text-center text-muted-foreground py-12">
							No bounties found matching your criteria.
						</CardContent>
					</Card>
				)}
				{filtered.map((bounty) => {
					const poster = users.get(bounty.posterId);
					return (
						<Link key={bounty.id} to={`/bounty/${bounty.id}`} className="block group">
							<Card className="transition-colors group-hover:border-primary/30 group-hover:shadow-sm">
								<CardContent className="pt-5 pb-5">
									<div className="flex items-start justify-between gap-4">
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2 mb-1.5">
												<StatusBadge status={bounty.status} />
												{bounty.tags.map((tag) => (
													<Badge key={tag} variant="secondary" className="text-xs">
														{tag}
													</Badge>
												))}
											</div>
											<h3 className="font-semibold text-base mb-1 group-hover:text-primary transition-colors">
												{bounty.title}
											</h3>
											<p className="text-sm text-muted-foreground line-clamp-1">
												{bounty.description}
											</p>
											<div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
												{poster && (
													<span>
														by <span className="font-medium text-foreground">{poster.username}</span>
													</span>
												)}
												<span className="flex items-center gap-1">
													<MessageSquare className="h-3 w-3" />
													{bounty.submissionCount} submissions
												</span>
												<span className="flex items-center gap-1">
													<Clock className="h-3 w-3" />
													{timeLeft(bounty.deadline)}
												</span>
											</div>
										</div>
										<div className="text-right shrink-0">
											<div className="text-2xl font-bold text-emerald-600">
												${bounty.bountyAmount}
											</div>
											<p className="text-xs text-muted-foreground">bounty</p>
										</div>
									</div>
								</CardContent>
							</Card>
						</Link>
					);
				})}
			</div>
		</div>
	);
}
